import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { pluralReadableTypeName, readableTypeName } from '@iotv/core-services';
import { DeviceConfig } from '@iotv/datamodel-core';
import { AdjacentType, isNonNaNNumber, isValidBusinessObject, objectsMatch } from '@iotv/iotv-v3-types';
import { extractEntitySchedulesFromNEM } from '@iotv/lfc-devices';
import { isDeviceEnabledType } from '@iotv/payload-definition';
import { Chip, chipClasses, List, ListItem, ListItemText, Skeleton, Stack, Typography } from '@mui/material';
import { useContext } from 'react';
import { ItemLink } from '../../../../../../../components/common/ItemLinkList';
import { NodeEdgeMapperContext } from '../../../../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { getNiceDate } from '../../../../../../../data/TypeHelpers';
import { AdminCardWrapper } from '../../../common/AdminCardWrapper';
import { StatusChip } from '../../../common/StatusChip';
import { StatusChipsKey } from '../../../common/StatusChipKey';
import { EntityProgrammeSetContext } from '../../Reducers/EntityProgrammeSetReducer';
const debug = process.env.REACT_APP_DEBUG && false;
export const EntityScheduledStatus = ({}) => {
    const [{ programmeSet }] = useContext(EntityProgrammeSetContext);
    const { state: { nodeEdgeMapper }, } = useContext(NodeEdgeMapperContext);
    const scheduledEntities = nodeEdgeMapper.getContainedItems().filter(isDeviceEnabledType);
    function renderEntityRow(entity) {
        const feedbackElements = [];
        if (entity && programmeSet) {
            let lastUpdated = null;
            const thisProgrammeSetSchedule = extractEntitySchedulesFromNEM({ entity, programmeSet, nodeEdgeMapper });
            const devices = nodeEdgeMapper.getNodesByPath(entity, [{ adjacencyType: AdjacentType.CHILD, objectTypeId: 'Device' }]);
            if (devices.length === 0) {
                debug && console.log(`WARN: NO DEVICES!`, nodeEdgeMapper.getSummary());
                const type = readableTypeName(entity.type ?? 'Entity');
                feedbackElements.push(_jsx(StatusChip, { label: "Schedule", state: 'failed', tooltip: `This ${type} doesn't have a device!` }, "schedule"));
                feedbackElements.push(_jsx(StatusChip, { label: "Requested", state: 'failed', tooltip: `This ${type} doesn't have a device!` }, "requested"));
                feedbackElements.push(_jsx(StatusChip, { label: "Reported", state: 'failed', tooltip: `This ${type} doesn't have a device!` }, "reported"));
            }
            if (devices.length === 1) {
                const deviceConfigs = nodeEdgeMapper.getAdjacentEdges(devices[0], { objectTypeId: 'DeviceConfig', adjacencyType: AdjacentType.CHILD });
                if (isValidBusinessObject(deviceConfigs?.at(0))) {
                    const { reported, requested, metadata, lastReportedMs, lastRequestedMs, lastRequestedBroadcastScheduleSk } = new DeviceConfig(deviceConfigs.at(0));
                    if (isNonNaNNumber(metadata?.lastUpdatedMs)) {
                        lastUpdated = metadata.lastUpdatedMs;
                    }
                    /** broadcastData */
                    const broadcastScheduleMatches = programmeSet?.sk === lastRequestedBroadcastScheduleSk;
                    const broadcastTimeNewer = programmeSet?.appliedTimestampsMs?.[0] !== undefined && programmeSet?.appliedTimestampsMs?.[0] <= (lastRequestedMs ?? 0);
                    let broadcastState = 'pending';
                    let broadcastTooltip = '';
                    if (broadcastScheduleMatches) {
                        if (broadcastTimeNewer) {
                            broadcastState = 'success';
                            broadcastTooltip = `This is the applied schedule${lastUpdated ? ` (last checked at ${getNiceDate(lastUpdated)})` : ''}`;
                        }
                        else {
                            broadcastState = 'error';
                            broadcastTooltip = `This is the applied schedule${programmeSet?.appliedTimestampsMs?.[0] && lastUpdated ? ` (changed at ${getNiceDate(programmeSet.appliedTimestampsMs[0])}, last checked at ${getNiceDate(lastUpdated)})` : ''}`;
                        }
                    }
                    else {
                        broadcastState = 'failed';
                        broadcastTooltip = 'This is not the applied schedule!';
                    }
                    feedbackElements.push(_jsx(StatusChip, { label: "Schedule", state: broadcastState, tooltip: broadcastTooltip }, "schedule"));
                    if (requested) {
                        const { scheduleSet } = requested;
                        const requestedMatch = objectsMatch(thisProgrammeSetSchedule?.scheduleSet ?? { never: true }, scheduleSet);
                        let requestedState = 'pending';
                        let requestedTooltip = '';
                        if (broadcastScheduleMatches) {
                            if (requestedMatch) {
                                requestedState = 'success';
                                requestedTooltip = `This does match the requested schedule${lastRequestedMs ? ` (last checked at ${getNiceDate(lastRequestedMs)})` : ''}`;
                            }
                            else {
                                requestedState = 'error';
                                requestedTooltip = `This doesn't match the requested schedule!${lastRequestedMs ? ` (last checked at ${getNiceDate(lastRequestedMs)})` : ''}`;
                            }
                        }
                        else {
                            requestedState = 'failed';
                            requestedTooltip = 'This is not the applied schedule!';
                        }
                        feedbackElements.push(_jsx(StatusChip, { label: "Requested", state: requestedState, tooltip: requestedTooltip }, "requested"));
                    }
                    else {
                        feedbackElements.push(_jsx(StatusChip, { label: "Requested", state: 'failed', tooltip: "This programme hasn't been requested!" }, "requested"));
                    }
                    if (reported) {
                        const { scheduleReport } = reported;
                        const reportedMatch = objectsMatch(thisProgrammeSetSchedule?.scheduleSet ?? { never: true }, scheduleReport?.config);
                        let reportedState = 'pending';
                        let reportedTooltip = '';
                        if (broadcastScheduleMatches) {
                            if (reportedMatch) {
                                reportedState = 'success';
                                reportedTooltip = `This does match the requested schedule${lastReportedMs ? ` (last checked at ${getNiceDate(lastReportedMs)})` : ''}`;
                            }
                            else {
                                reportedState = 'error';
                                reportedTooltip = `This doesn't match the requested schedule!${lastReportedMs ? ` (last checked at ${getNiceDate(lastReportedMs)})` : ''}`;
                            }
                        }
                        else {
                            reportedState = 'failed';
                            reportedTooltip = 'This is not the applied schedule!';
                        }
                        feedbackElements.push(_jsx(StatusChip, { label: "Reported", state: reportedState, tooltip: reportedTooltip }, "reported"));
                    }
                    else {
                        feedbackElements.push(_jsx(StatusChip, { label: "Reported", state: 'failed', tooltip: "The device hasn't reported a programme!" }, "reported"));
                    }
                }
            }
        }
        return (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemText, { children: _jsx(Chip, { label: _jsxs(Stack, { direction: 'row', justifyContent: 'space-between', gap: 2, children: [_jsx(ItemLink, { item: entity, underscoreLinks: true }), _jsx(Stack, { direction: 'row', alignItems: 'center', gap: 0.5, children: feedbackElements })] }), sx: {
                        width: 1,
                        justifyContent: 'unset',
                        [`& > .${chipClasses.label}`]: {
                            flexGrow: 1,
                            paddingInlineEnd: 0.6,
                        },
                    } }) }) }, entity.id));
    }
    return (_jsxs(AdminCardWrapper, { children: [_jsxs(Stack, { direction: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2, children: [_jsxs(Typography, { variant: "h6", children: [`Scheduled ${pluralReadableTypeName(programmeSet?.supportedType ?? 'Entity')}`, ":"] }), _jsx(StatusChipsKey, {})] }), programmeSet && scheduledEntities ? _jsx(List, { children: scheduledEntities.map(renderEntityRow) }) : _jsx(Skeleton, { variant: "rounded", height: '2em' })] }));
};
